import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import type { IsoCamera } from '@/core/IsoCamera';
import { PALETTE } from '@/art/palette';

/**
 * Fysieke terugkoppeling: schudden, kort bevriezen, mondingsvuur.
 *
 * Schieten en aanrijden gaven tot nu toe alleen geluid. Er zat geen enkel signaal in het beeld
 * dat er iets raakte, waardoor het spel als een simulatie aanvoelde in plaats van als een
 * gevecht.
 *
 * Dit systeem hangt volledig aan de `EventBus`, net als `audio/Sfx.ts`. De gameplay-code weet
 * er niets van en hoefde er ook niet voor te veranderen — de events die het nodig heeft
 * bestonden allemaal al.
 */

export const FEEDBACK = {
  /**
   * Trauma per gebeurtenis. Zie `CAMERA_SHAKE` voor hoe dat naar uitslag vertaalt.
   *
   * Afgesteld op gemeten beeldverplaatsing bij speelzoom (~13 px per meter): een schot ~2 px,
   * een treffer ~3 px, veertig schade ~5 px, een aanrijding ~10 px. Zo blijft de rangorde
   * voelbaar zonder dat een vuurgevecht onleesbaar wordt.
   */
  shake: {
    shot: 0.30,
    impactWall: 0.22,
    impactTarget: 0.38,
    /** Per punt schade; een volle magazijnlading voelt zwaarder dan één kogel. */
    damagePerPoint: 0.015,
    damageMax: 0.72,
    /** Per m/s aanrijdsnelheid. */
    collisionPerSpeed: 0.075,
    collisionMax: 0.85,
    ranOver: 0.9,
  },
  /**
   * Hoe lang de simulatie stilstaat. Kort: dit moet als klap lezen, niet als hapering.
   * Boven ~0.12 s begint het als een framedrop te voelen.
   */
  hitstop: {
    kill: 0.055,
    ranOver: 0.09,
  },
  muzzleFlash: {
    intensity: 26,
    range: 9,
    /** Seconden. Eén oogwenk — langer en het leest als een lamp in plaats van een flits. */
    duration: 0.055,
    height: 1.2,
    /**
     * Hoeveel flitsen er tegelijk mogen branden.
     *
     * three.js compileert een shaderprogramma per lampconfiguratie, dus elk nieuw aantal
     * actieve lampen kost eenmalig een hercompilatie. Met een dak erop blijft dat aantal
     * varianten klein en voorspelbaar: de pool doet er maximaal twaalf, hier komen er hoogstens
     * deze bij. Meer dan twee tegelijk voegt visueel niets toe — een vuurgevecht leest van de
     * dichtstbijzijnde flits.
     */
    maxConcurrent: 2,
  },
} as const;

interface Flash {
  readonly light: THREE.PointLight;
  remaining: number;
}

export class Feedback {
  readonly #camera: IsoCamera;
  readonly #scene: THREE.Scene;
  readonly #listeners: Array<() => void> = [];
  readonly #flashes: Flash[] = [];
  readonly #idle: THREE.PointLight[] = [];
  #freeze = 0;

  constructor(scene: THREE.Scene, camera: IsoCamera) {
    this.#scene = scene;
    this.#camera = camera;
    this.#subscribe();
  }

  /**
   * Staat de simulatie stil?
   *
   * `main.ts` slaat op grond hiervan zijn simulatieblok over. Bewust geen schaalfactor op `dt`:
   * `core/Loop.ts` ontleent zijn determinisme aan een vaste stap van exact 1/60, en die wil je
   * niet aantasten voor een visueel effect. Stappen overslaan houdt die stap intact.
   */
  get frozen(): boolean {
    return this.#freeze > 0;
  }

  /** Actieve mondingsflitsen. Voor de debug-overlay en het lichtbudget. */
  get activeFlashes(): number {
    return this.#flashes.length;
  }

  /** Loopt door terwijl de simulatie bevroren is — anders zou de hitstop nooit aflopen. */
  update(dt: number): void {
    if (this.#freeze > 0) this.#freeze = Math.max(0, this.#freeze - dt);
    this.#camera.update(dt);

    for (let index = this.#flashes.length - 1; index >= 0; index--) {
      const flash = this.#flashes[index]!;
      flash.remaining -= dt;
      if (flash.remaining > 0) {
        // Snel uitdoven in plaats van hard uitgaan; dat leest als een flits.
        flash.light.intensity = FEEDBACK.muzzleFlash.intensity
          * (flash.remaining / FEEDBACK.muzzleFlash.duration);
        continue;
      }
      this.#scene.remove(flash.light);
      this.#flashes.splice(index, 1);
      this.#idle.push(flash.light);
    }
  }

  dispose(): void {
    for (const off of this.#listeners) off();
    this.#listeners.length = 0;
    for (const flash of this.#flashes) this.#scene.remove(flash.light);
    this.#flashes.length = 0;
    this.#idle.length = 0;
  }

  #subscribe(): void {
    const shake = FEEDBACK.shake;
    this.#listeners.push(
      bus.on('combat:shot', (event) => {
        this.#camera.shake(shake.shot);
        this.#flash(event.x, event.z);
      }),
      bus.on('combat:impact', (event) => {
        this.#camera.shake(event.outcome === 'target' ? shake.impactTarget : shake.impactWall);
      }),
      bus.on('player:damaged', (event) => {
        this.#camera.shake(Math.min(shake.damageMax, event.amount * shake.damagePerPoint));
      }),
      bus.on('enemy:killed', () => this.#freezeFor(FEEDBACK.hitstop.kill)),
      bus.on('vehicle:damaged', (event) => {
        if (event.reason !== 'collision') return;
        this.#camera.shake(Math.min(shake.collisionMax, event.speed * shake.collisionPerSpeed));
      }),
      bus.on('vehicle:ranOver', () => {
        this.#camera.shake(shake.ranOver);
        this.#freezeFor(FEEDBACK.hitstop.ranOver);
      }),
    );
  }

  #freezeFor(seconds: number): void {
    this.#freeze = Math.max(this.#freeze, seconds);
  }

  /**
   * Mondingsvuur. Blijft bewust buiten `LightPool`: die pool is voor statische stadslampen met
   * afstands-culling en een hard maximum van twaalf, en een flits van 55 ms hoort daar niet
   * doorheen te breken. De lampen worden hergebruikt zodat een vuurgevecht geen allocaties
   * veroorzaakt.
   */
  #flash(x: number, z: number): void {
    if (this.#flashes.length >= FEEDBACK.muzzleFlash.maxConcurrent) {
      // Vol: hergebruik de oudste in plaats van er een lamp bij te zetten. Het aantal actieve
      // lampen blijft daarmee gelijk, dus er volgt geen shaderhercompilatie.
      const oldest = this.#flashes.shift()!;
      oldest.light.position.set(x, FEEDBACK.muzzleFlash.height, z);
      oldest.light.intensity = FEEDBACK.muzzleFlash.intensity;
      oldest.remaining = FEEDBACK.muzzleFlash.duration;
      this.#flashes.push(oldest);
      return;
    }

    const light = this.#idle.pop() ?? this.#createFlashLight();
    light.position.set(x, FEEDBACK.muzzleFlash.height, z);
    light.intensity = FEEDBACK.muzzleFlash.intensity;
    this.#scene.add(light);
    this.#flashes.push({ light, remaining: FEEDBACK.muzzleFlash.duration });
  }

  #createFlashLight(): THREE.PointLight {
    const light = new THREE.PointLight(
      PALETTE.sodiumAmber,
      FEEDBACK.muzzleFlash.intensity,
      FEEDBACK.muzzleFlash.range,
      2,
    );
    light.name = 'NA_Light_MuzzleFlash';
    return light;
  }
}
